const RELATIVE_DATE_OPERATORS = new Set([
    'inCurrentMonth',
    'inCurrentQuarter',
    'inCurrentYear',
    'lastMonth',
    'lastNDays',
    'lastQuarter',
    'lastWeek',
    'lastYear',
    'thisWeek',
    'withinNextDays',
]);
/**
 * Returns `true` when the operator is a relative-date operator that must be
 * resolved into a Cube.js `timeDimensions.dateRange` entry before querying.
 */
export const isRelativeDateOperator = (operator) => RELATIVE_DATE_OPERATORS.has(operator);
/**
 * Maps a relative-date filter to its native Cube.js `dateRange` string.
 */
const toDateRange = ({ operator, values }) => {
    switch (operator) {
        case 'inCurrentMonth':
            return 'this month';
        case 'inCurrentQuarter':
            return 'this quarter';
        case 'inCurrentYear':
            return 'this year';
        case 'thisWeek':
            return 'this week';
        case 'lastWeek':
            return 'last week';
        case 'lastMonth':
            return 'last month';
        case 'lastQuarter':
            return 'last quarter';
        case 'lastYear':
            return 'last year';
        case 'lastNDays': {
            const days = Math.max(1, parseInt(values?.[0] ?? '1', 10) || 1);
            return `last ${days} days`;
        }
        case 'withinNextDays': {
            const days = Math.max(1, parseInt(values?.[0] ?? '1', 10) || 1);
            return `next ${days} days`;
        }
        default:
            return 'this month';
    }
};
/**
 * Transforms a report query by extracting relative-date filters and routing
 * them into Cube.js `timeDimensions` with native `dateRange` strings.
 *
 * Regular (non-relative) filters pass through unchanged. If a timeDimension
 * entry already exists for the dimension (e.g. for granularity grouping), its
 * `dateRange` is set; otherwise a new entry is appended.
 */
export const resolveRelativeDateFilters = (query) => {
    const { filters } = query;
    if (!filters || filters.length === 0)
        return query;
    const regularFilters = [];
    const relativeFilters = [];
    for (const filter of filters) {
        if (isRelativeDateOperator(filter.operator)) {
            relativeFilters.push(filter);
        }
        else {
            const { isRuntimeParameter: _, ...cubeFilter } = filter;
            regularFilters.push(cubeFilter);
        }
    }
    if (relativeFilters.length === 0) {
        return {
            ...query,
            filters: regularFilters.length > 0 ? regularFilters : undefined,
        };
    }
    const timeDimensions = [...(query.timeDimensions ?? [])];
    const indexByDimension = new Map(timeDimensions.map((td, i) => [td.dimension, i]));
    for (const filter of relativeFilters) {
        const dateRange = toDateRange(filter);
        const existingIndex = indexByDimension.get(filter.member);
        if (existingIndex !== undefined) {
            timeDimensions[existingIndex] = { ...timeDimensions[existingIndex], dateRange };
        }
        else {
            indexByDimension.set(filter.member, timeDimensions.length);
            timeDimensions.push({ dimension: filter.member, dateRange });
        }
    }
    return {
        ...query,
        filters: regularFilters.length > 0 ? regularFilters : undefined,
        timeDimensions: timeDimensions.length > 0 ? timeDimensions : undefined,
    };
};
